import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react';
import { can, type Capability, type WorkspaceMembershipSummary } from '@floorpoll/shared';
import { useAuth } from './auth.js';

const ACTIVE_WORKSPACE_KEY = 'floorpoll.activeWorkspaceId';

interface WorkspaceState {
  active: WorkspaceMembershipSummary | null;
  workspaces: WorkspaceMembershipSummary[];
  setActive: (workspaceId: string) => void;
  /** Mirrors the server-side capability matrix so the UI hides what would 403. */
  can: (capability: Capability) => boolean;
  /**
   * Capability in a specific workspace. Views that open a resource by id must
   * use this rather than `can`: the resource may belong to a workspace other
   * than the active one, where the user holds a different role.
   */
  canIn: (workspaceId: string | undefined, capability: Capability) => boolean;
}

const WorkspaceContext = createContext<WorkspaceState | null>(null);

/**
 * Accepting an invitation means a user can belong to several workspaces, so
 * every console view is scoped to one active workspace (spec §4).
 */
export function WorkspaceProvider({ children }: { children: ReactNode }) {
  const { workspaces } = useAuth();
  const [activeId, setActiveId] = useState<string | null>(() =>
    localStorage.getItem(ACTIVE_WORKSPACE_KEY),
  );

  // Falls back to the first workspace when the stored id is stale — for
  // instance after being removed from that workspace.
  const active = useMemo(
    () => workspaces.find((w) => w.workspaceId === activeId) ?? workspaces[0] ?? null,
    [workspaces, activeId],
  );

  const setActive = useCallback((workspaceId: string) => {
    localStorage.setItem(ACTIVE_WORKSPACE_KEY, workspaceId);
    setActiveId(workspaceId);
  }, []);

  const value = useMemo<WorkspaceState>(
    () => ({
      active,
      workspaces,
      setActive,
      can: (capability) => (active ? can(active.role, capability) : false),
      canIn: (workspaceId, capability) => {
        const membership = workspaces.find((w) => w.workspaceId === workspaceId);
        return membership ? can(membership.role, capability) : false;
      },
    }),
    [active, workspaces, setActive],
  );

  return <WorkspaceContext.Provider value={value}>{children}</WorkspaceContext.Provider>;
}

export function useWorkspace(): WorkspaceState {
  const context = useContext(WorkspaceContext);
  if (!context) throw new Error('useWorkspace must be used inside a WorkspaceProvider');
  return context;
}
